import useDebouncedPromise from '@/hooks/useDebouncedPromise';
import React, { useRef, useCallback, useMemo, useState } from 'react';
import Select, {
  components,
  OptionProps,
  DropdownIndicatorProps,
  StylesConfig,
  GroupBase,
  MenuPosition,
} from 'react-select';
import AsyncSelect from 'react-select/async';

export interface SelectOption {
  label: string;
  value: string | number;
}

interface ArtisanalSelectProps {
  options?: SelectOption[];
  loadOptions?: (inputValue: string) => Promise<SelectOption[]>;
  /** Time in milliseconds to wait after typing stops before calling loadOptions */
  debounceInterval?: number;
  value?: SelectOption | SelectOption[] | null;
  onChange?: (value: any) => void;
  placeholder?: string;
  isMulti?: boolean;
  isDisabled?: boolean;
  /** Custom render function for the option label */
  formatOptionLabel?: (data: SelectOption, props: any) => React.ReactNode;
  menuPortalTarget?: HTMLElement | null | undefined;
  menuPosition?: MenuPosition | undefined;
}

/**
 * Higher-Order Function: Generates a Custom Option component.
 * Takes an optional formatOptionLabel function to render custom content next to the radio button.
 */
const createCustomOption = (
  formatOptionLabel?: (data: SelectOption, props: any) => React.ReactNode,
) => {
  return (
    props: OptionProps<SelectOption, boolean, GroupBase<SelectOption>>,
  ) => {
    const { isSelected, isFocused, label, data } = props;

    return (
      <components.Option {...props}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Radio Button (Always rendered) */}
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `2px solid ${
                isSelected
                  ? 'var(--color-brand-burgundy, #000)'
                  : isFocused
                    ? 'var(--color-brand-truffle, #666)'
                    : 'var(--color-brand-frost, #ccc)'
              }`,
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-brand-burgundy, #000)',
                transform: isSelected ? 'scale(1)' : 'scale(0)',
                transition:
                  'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            />
          </div>

          {/* Dynamic Content Area */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: 1, // Allows content to take up remaining space
            }}
          >
            {/* If formatOptionLabel is provided, use it. Otherwise, fallback to default single-line. */}
            {formatOptionLabel ? (
              formatOptionLabel(data, props)
            ) : (
              <span
                style={{
                  fontWeight: isSelected ? 600 : 500,
                  color: '#000000',
                  transition: 'color 0.2s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.2',
                }}
              >
                {label}
              </span>
            )}
          </div>
        </div>
      </components.Option>
    );
  };
};

/**
 * Custom Spyglass Search Icon for Async Select
 */
const SearchDropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, boolean, GroupBase<SelectOption>>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-text-muted)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
    </components.DropdownIndicator>
  );
};

export const ArtisanalSelect: React.FC<ArtisanalSelectProps> = ({
  options,
  loadOptions,
  debounceInterval = 500, // Default to half a second
  onChange = undefined,
  formatOptionLabel,
  ...props
}) => {
  const isAsync = Boolean(loadOptions);
  const SelectComponent = isAsync ? AsyncSelect : Select;
  // 1. Add state to control the typed text
  const [inputValue, setInputValue] = useState('');

  // Wrap the provided loadOptions function in our debouncer
  const debouncedLoadOptions = useDebouncedPromise(
    loadOptions,
    debounceInterval,
  );

  // 1. Update your custom components to have the white "Pill" aesthetic
  const portalTrayCustomComponents = useMemo(
    () => ({
      Option: createCustomOption(formatOptionLabel),

      NoOptionsMessage: () => (
        <div className="p-4 text-center text-gray-500 font-[500] text-sm italic bg-white rounded-[var(--radius-artisan)] border border-brand-truffle/10 shadow-[0_1px_3px_rgba(85,75,65,0.12)]">
          No addresses found. Try a different search.
        </div>
      ),

      LoadingMessage: () => (
        <div className="p-4 flex items-center justify-center gap-2 text-black/60 bg-white rounded-[var(--radius-artisan)] border border-brand-truffle/10 shadow-[0_1px_3px_rgba(85,75,65,0.12)]">
          <div className="h-4 w-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
          <span className="text-sm font-[500]">Searching...</span>
        </div>
      ),

      ...(isAsync
        ? {
            DropdownIndicator: SearchDropdownIndicator,
            IndicatorSeparator: () => null,
          }
        : {}),
    }),
    [isAsync],
  );

  // Define component overrides
  const customComponents = useMemo(
    () => ({
      Option: createCustomOption(formatOptionLabel),
      // Custom Aesthetic Empty State
      NoOptionsMessage: () => (
        <div className="p-4 text-center text-gray-500 font-[500] text-sm italic">
          No addresses found. Try a different search.
        </div>
      ),
      // Custom Aesthetic Loading State
      LoadingMessage: () => (
        <div className="p-4 flex items-center justify-center gap-2 text-black/60">
          <div className="h-4 w-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
          <span className="text-sm font-[500]">Searching...</span>
        </div>
      ),
      // Swap the dropdown arrow for a spyglass if we are in async search mode
      ...(isAsync
        ? {
            DropdownIndicator: SearchDropdownIndicator,
            IndicatorSeparator: () => null,
          }
        : {}),
    }),
    [isAsync],
  );

  // 2. Intercept changes to update the input text with the selected label
  const handleChange = (selectedOption: any, actionMeta: any) => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else {
      setInputValue(''); // Handle clearing
    }

    // Pass the value up to the parent component (DeliveryAddress)
    if (onChange) {
      onChange(selectedOption);
    }
  };

  const selectStyles: StylesConfig<
    SelectOption,
    boolean,
    GroupBase<SelectOption>
  > = {
    control: (base, state) => ({
      ...base,
      backgroundColor: state.isDisabled
        ? '#F9FAFB'
        : 'var(--color-brand-cream)',
      borderRadius: 'var(--radius-artisan)',
      minHeight: '48px',
      padding: '0 4px',
      boxShadow: state.isFocused
        ? ' 0 1px 3px rgba(85, 75, 65, 0.12), 0 1px 2px rgba(85, 75, 65, 0.2)'
        : '',
      borderWidth: '1px',
      borderColor: state.isDisabled
        ? '#E5E7EB'
        : state.isFocused
          ? 'var(--color-brand-frost)'
          : 'var(--color-brand-truffle)',
      fontFamily: 'var(--font-display)',
      '&:hover': {
        borderColor: 'var(--color-brand-truffle)',
      },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      border: 'none',
      marginTop: '8px',
      zIndex: 50,
      // 1. THE WIDTH FIX
      // Revert this to 100% so it perfectly hugs the input boundaries
      // and brings the scrollbar back onto the screen.
      width: '100%',
    }),
    menuList: (base) => ({
      ...base,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      // 2. THE PADDING FIX
      // Added 16px top padding so the first item can scroll UP into the top fade zone
      padding: '16px 4px 24px 4px',
      maxHeight: '260px',

      // Custom Artisanal Scrollbar
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'var(--color-brand-truffle)',
        borderRadius: '10px',
      },

      // 3. THE DUAL-FADE MASK
      // 0-10%: Fades in at the top
      // 10-85%: Solid black (fully visible)
      // 85-100%: Fades out at the bottom
      WebkitMaskImage:
        'linear-gradient(to bottom, transparent 0%, black 10%, black 85%, transparent 100%)',
      maskImage:
        'linear-gradient(to bottom, transparent 0%, black 10%, black 85%, transparent 100%)',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: 'white',
      borderRadius: 'var(--radius-artisan)',
      border: `${
        state.isSelected
          ? '2px solid var(--color-brand-sage)'
          : state.isFocused
            ? '2px solid var(--color-brand-frost)'
            : '1px solid var(--color-brand-truffle)'
      }`,
      padding: '12px 16px',
      color: 'var(--color-text-main)',
      cursor: 'pointer',
      boxShadow:
        state.isFocused || state.isSelected
          ? '0 3px 6px rgba(85, 75, 65, 0.12), 0 3px 6px rgba(85, 75, 65, 0.2)'
          : '0 1px 3px rgba(85, 75, 65, 0.12), 0 1px 2px rgba(85, 75, 65, 0.2)',
      transform: state.isFocused ? 'translateY(-2px)' : 'none',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: 'var(--font-display)',
      '&:active': {
        backgroundColor: 'var(--color-brand-cream)',
        transform: 'translateY(0)',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: 'var(--color-text-muted)',
    }),
    // 1. Fix: Ensure typed input text is solid black
    input: (base) => ({
      ...base,
      // Force the container to remain visible
      opacity: '1 !important',
      color: '#000000 !important',

      // Target the actual underlying HTML <input> element generated by react-select
      '& input': {
        opacity: '1 !important',
        color: '#000000 !important',
      },

      // Target the data attribute react-select sometimes uses
      '[data-value]': {
        opacity: '1 !important',
      },
    }),
    // 1. Fix: Ensure the selected option label is solid black instead of inheriting an invisible color
    singleValue: (base) => ({
      ...base,
      color: 'var(--color-text-main)',
      fontWeight: 500,
    }),
  };

  return (
    <SelectComponent
      styles={selectStyles}
      components={
        !props?.menuPortalTarget ? customComponents : portalTrayCustomComponents
      }
      options={options as any}
      loadOptions={isAsync ? (debouncedLoadOptions as any) : undefined}
      classNamePrefix="artisan-select"
      isSearchable={true} // Explicitly ensure typing is enabled
      // 3. Bind the controlled input values
      inputValue={inputValue}
      onInputChange={(newValue, actionMeta) => {
        // Prevent react-select from clearing the input when the menu closes
        if (
          actionMeta.action !== 'input-blur' &&
          actionMeta.action !== 'menu-close'
        ) {
          setInputValue(newValue);
        }
      }}
      onChange={handleChange}
      // Hide the singleValue so the input acts as the sole text display
      controlShouldRenderValue={false}
      {...props}
    />
  );
};
